import React, { useState, useEffect, useCallback, useRef } from 'react';
import { quotationService } from '../../service/Vendorservisefile/quotationService.jsx';
import { inquiryService } from '../../service/Vendorservisefile/inquiryService';
import './SalseQuotation.css';

const InquiryComponent = () => {
  // State for inquiries data
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // State for popup visibility and selected inquiry
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  // State for view modal
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewInquiry, setViewInquiry] = useState(null);

  const [sentQuotations, setSentQuotations] = useState(() => {
    const saved = localStorage.getItem("sentQuotations");
    return saved ? JSON.parse(saved) : {};
  });

  // State for form data
  const [formData, setFormData] = useState({
    subject: "",
    amount: "",
    validity: "",
    message: "",
    document: null,
    documentName: "",
    deliveryDays: ""
  });

  // File input ref
  const fileInputRef = useRef(null);

  // Vendor ID
  const VENDOR_ID = 1;

  // Fetch inquiries
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all inquiries
        const inquiriesData = await inquiryService.getAllInquiries();

        const filteredData = inquiriesData.filter(
          (item) => item.projectName && item.projectName.trim() !== ""
        );

        setInquiries(filteredData);

        // Load sent quotations from localStorage
        const localSent = JSON.parse(
          localStorage.getItem("sentQuotations") || "{}"
        );

        try {
          // Fetch quotations from API
          const quotationsData = await quotationService.getVendorQuotations(
            VENDOR_ID
          );

          const sentMap = {};

          if (Array.isArray(quotationsData)) {
            quotationsData.forEach((quote) => {
              if (quote.enquiryId) {
                sentMap[quote.enquiryId] = true;
              }
            });
          }

          // Merge API + LocalStorage
          const mergedSent = {
            ...localSent,
            ...sentMap,
          };

          setSentQuotations(mergedSent);

          localStorage.setItem(
            "sentQuotations",
            JSON.stringify(mergedSent)
          );
        } catch (quoteError) {
          console.log(
            "Could not fetch quotations, using localStorage only"
          );

          // Use only localStorage if API fails
          setSentQuotations(localSent);
        }

        setLoading(false);
      } catch (err) {
        console.error(err);

        setError(err.message || "Failed to load inquiries");

        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format date for API
  const formatDateForApi = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Format budget range
  const formatBudget = (budgetRange) => {
    if (!budgetRange) return 'Not specified';
    const budgetMap = {
      'BELOW_50000': 'Below ₹50,000',
      'BETWEEN_50000_AND_100000': '₹50,000 - ₹1,00,000',
      'BETWEEN_100000_AND_500000': '₹1,00,000 - ₹5,00,000',
      'BETWEEN_500000_AND_1000000': '₹5,00,000 - ₹10,00,000',
      'ABOVE_1000000': 'Above ₹10,00,000'
    };
    return budgetMap[budgetRange] || budgetRange || 'Not specified';
  };

  // Format category
  const formatCategory = (category) => {
    if (!category) return 'N/A';
    const categoryMap = {
      'ELECTRONICS': 'Electronics',
      'MANUFACTURING': 'Manufacturing',
      'AEROSPACE': 'Aerospace',
      'ENERGY': 'Energy',
      'OTHER': 'Other'
    };
    return categoryMap[category] || category;
  };

  // Format processes
  const formatProcesses = (processes) => {
    if (!processes || processes.length === 0) return 'N/A';
    const processMap = {
      'PRINTING_3D': '3D Printing',
      'CNC_MACHINING': 'CNC Machining',
      'WELDING_FABRICATION': 'Welding Fabrication',
      'SHEET_METAL_FABRICATION': 'Sheet Metal Fabrication',
      'ELECTRONICS_PCB': 'Electronics PCB Assembly'
    };
    return processes.map(p => processMap[p] || p).join(', ');
  };

  // Format materials
  const formatMaterials = (materials) => {
    if (!materials || materials.length === 0) return 'N/A';
    const materialMap = {
      'PLASTIC': 'Plastic',
      'ALUMINIUM': 'Aluminium',
      'STEEL': 'Steel',
      'METAL': 'Metal',
      'STAINLESS_STEEL': 'Stainless Steel',
      'ABS': 'ABS'
    };
    return materials.map(m => materialMap[m] || m).join(', ');
  };

  // Format stage
  const formatStage = (stage) => {
    if (!stage) return 'N/A';
    const stageMap = {
      'DESIGN': 'Design Phase',
      'WORKING_PROTOTYPE': 'Working Prototype',
      'PRODUCTION': 'Production'
    };
    return stageMap[stage] || stage;
  };

  // Handlers
  const handleOpenPopup = (inquiry) => {
    setSelectedInquiry(inquiry);
    const today = new Date();
    const validUntil = new Date(today);
    validUntil.setDate(today.getDate() + 14);

    setFormData({
      subject: `Quotation: ${inquiry.projectName}`,
      amount: '',
      validity: formatDateForApi(validUntil),
      message: `Dear Customer,\n\nThank you for your inquiry regarding "${inquiry.projectName}".\n\nWe are pleased to provide you with our quotation. Please find the details below and let us know if you have any questions.\n\nLooking forward to working with you!`,
      document: null,
      documentName: "",
      deliveryDays: "20"
    });
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedInquiry(null);
    setFormData({
      subject: "",
      amount: "",
      validity: "",
      message: "",
      document: null,
      documentName: "",
      deliveryDays: ""
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenViewModal = (inquiry) => {
    setViewInquiry(inquiry);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewInquiry(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.pdf') && !file.name.endsWith('.doc') && !file.name.endsWith('.docx')) {
        alert('Please upload a PDF or Word document.');
        e.target.value = '';
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size should be less than 10MB.');
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        document: file,
        documentName: file.name
      }));
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      document: null,
      documentName: ""
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedInquiry) return;

    // Validation
    if (!formData.amount || Number(formData.amount) <= 0) {
      alert("Please enter a valid quotation amount.");
      return;
    }

    try {
      setSubmitting(true);

      // Prepare quotation data
      const quotationData = {
        enquiryId: selectedInquiry.id,
        vendorId: VENDOR_ID,
        quotationAmount: Number(formData.amount),
        estimatedDeliveryDays: Number(formData.deliveryDays) || 20,
        validUntil:
          formData.validity ||
          new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
        message:
          formData.message ||
          `Quotation for ${selectedInquiry.projectName}`,
      };

      // Send quotation
      const response = await quotationService.sendQuotation(
        quotationData,
        formData.document
      );

      console.log("Quotation Response:", response);

      // Update sent quotations
      const updatedSent = {
        ...sentQuotations,
        [selectedInquiry.id]: true,
      };

      setSentQuotations(updatedSent);

      // Save in localStorage
      localStorage.setItem(
        "sentQuotations",
        JSON.stringify(updatedSent)
      );

      // Success message
      alert(
        `✅ Quotation Sent Successfully!

Project: ${selectedInquiry.projectName}

Amount: ₹${quotationData.quotationAmount}

Delivery Days: ${quotationData.estimatedDeliveryDays}

Valid Until: ${quotationData.validUntil}

${
  formData.document
    ? `Document: ${formData.document.name}`
    : "No Document Attached"
}`
      );

      // Close popup
      handleClosePopup();
    } catch (error) {
      console.error("Quotation Error:", error);

      alert(
        "❌ Failed to send quotation.\n\n" +
          (error.message || "Something went wrong.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Reload data
  const reloadData = useCallback(async () => {
    try {
      setLoading(true);
      const inquiriesData = await inquiryService.getAllInquiries();
      const filteredData = inquiriesData.filter(item =>
        item.projectName && item.projectName.trim() !== ''
      );
      setInquiries(filteredData);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="inquiry-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading inquiries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inquiry-container">
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <p>Error: {error}</p>
          <button onClick={reloadData} className="btn btn-retry">
            <i className="fas fa-sync"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="inquiry-container">
      {/* Header */}
      <div className="inquiry-page-header">
        <h1 className="page-title">
          <i className="fas fa-file-invoice"></i>
          Customer Inquiries
          <span className="inquiry-count">{inquiries.length} inquiries</span>
        </h1>
        <div className="header-actions">
          <button className="btn btn-refresh" onClick={reloadData} title="Refresh">
            <i className="fas fa-sync"></i> Refresh
          </button>
        </div>
      </div>

      {inquiries.length === 0 ? (
        <div className="no-inquiries">
          <i className="fas fa-inbox"></i>
          <p>No inquiries found</p>
          <button className="btn btn-primary" onClick={reloadData}>
            <i className="fas fa-sync"></i> Refresh
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="inquiry-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Project Name</th>
                <th>Quantity</th>
                <th>Category</th>
                <th>Budget</th>
                <th>Delivery Date</th>
                <th>Processes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inquiry) => (
                <tr key={inquiry.id}>
                  <td data-label="ID">#{inquiry.id}</td>
                  <td data-label="Project Name" className="project-name-cell">{inquiry.projectName}</td>
                  <td data-label="Quantity">{inquiry.requiredQuantity || 'N/A'} units</td>
                  <td data-label="Category">{formatCategory(inquiry.productCategory)}</td>
                  <td data-label="Budget">{formatBudget(inquiry.expectedBudgetRange)}</td>
                  <td data-label="Delivery Date">{formatDate(inquiry.targetDeliveryDate)}</td>
                  <td data-label="Processes" className="processes-cell">{formatProcesses(inquiry.manufacturingProcesses)}</td>
                  <td data-label="Actions">
                    <div className="table-actions">
                      <button
                        className="btn btn-view"
                        onClick={() => handleOpenViewModal(inquiry)}
                        title="View inquiry details"
                      >
                        <i className="fas fa-eye"></i> View
                      </button>
                      {sentQuotations[inquiry.id] ? (
                        <span className="btn btn-sent" title="Quotation already sent">
                          <i className="fas fa-check-circle"></i> Sent
                        </span>
                      ) : (
                        <button
                          className="btn btn-send"
                          onClick={() => handleOpenPopup(inquiry)}
                          title="Send quotation"
                        >
                          <i className="fas fa-paper-plane"></i> Send Quote
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && viewInquiry && (
        <div className="popup-overlay" onClick={handleCloseViewModal}>
          <div className="popup-card view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3 className="popup-title">
                <i className="fas fa-file-invoice"></i>
                Inquiry Details #{viewInquiry.id}
              </h3>
              <button className="close-popup" onClick={handleCloseViewModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="view-modal-content">
              <div className="detail-section">
                <h4><i className="fas fa-info-circle"></i> Basic Information</h4>
                <div className="detail-row">
                  <span className="detail-label">Project Name:</span>
                  <span className="detail-value">{viewInquiry.projectName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Required Quantity:</span>
                  <span className="detail-value">{viewInquiry.requiredQuantity || 'N/A'} units</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Short Description:</span>
                  <span className="detail-value">{viewInquiry.shortDescription || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Detailed Description:</span>
                  <span className="detail-value">{viewInquiry.detailedProjectDescription || 'N/A'}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4><i className="fas fa-cogs"></i> Project Specifications</h4>
                <div className="detail-row">
                  <span className="detail-label">Category:</span>
                  <span className="detail-value">{formatCategory(viewInquiry.productCategory)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Industry Sector:</span>
                  <span className="detail-value">{viewInquiry.industrySector || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Development Stage:</span>
                  <span className="detail-value">{formatStage(viewInquiry.developmentStage)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Budget Range:</span>
                  <span className="detail-value">{formatBudget(viewInquiry.expectedBudgetRange)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Target Delivery:</span>
                  <span className="detail-value">{formatDate(viewInquiry.targetDeliveryDate)}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4><i className="fas fa-map-marker-alt"></i> Location & Requirements</h4>
                <div className="detail-row">
                  <span className="detail-label">Preferred Location:</span>
                  <span className="detail-value">{viewInquiry.preferredLocation || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Manufacturing Processes:</span>
                  <span className="detail-value">{formatProcesses(viewInquiry.manufacturingProcesses)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Required Materials:</span>
                  <span className="detail-value">{formatMaterials(viewInquiry.requiredMaterials)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Manufacturing Requirements:</span>
                  <span className="detail-value">{viewInquiry.manufacturingRequirements || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Special Requirements:</span>
                  <span className="detail-value">{viewInquiry.specialRequirements || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Additional Notes:</span>
                  <span className="detail-value">{viewInquiry.additionalNotes || 'N/A'}</span>
                </div>
              </div>

              {viewInquiry.enquiryFiles && viewInquiry.enquiryFiles.length > 0 && (
                <div className="detail-section">
                  <h4><i className="fas fa-paperclip"></i> Attachments</h4>
                  <div className="file-list">
                    {viewInquiry.enquiryFiles.map((file, index) => (
                      <a key={index} href={file} target="_blank" rel="noopener noreferrer" className="file-link">
                        <i className="fas fa-file"></i>
                        Attachment {index + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="view-modal-actions">
              <button className="btn btn-cancel" onClick={handleCloseViewModal}>
                Close
              </button>
              {!sentQuotations[viewInquiry.id] && (
                <button className="btn btn-submit" onClick={() => {
                  handleCloseViewModal();
                  handleOpenPopup(viewInquiry);
                }}>
                  <i className="fas fa-paper-plane"></i> Send Quotation
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Send Quotation Popup with PDF Upload */}
      {isPopupOpen && selectedInquiry && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div className="popup-card" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3 className="popup-title">
                <i className="fas fa-file-signature"></i> Send Quotation
              </h3>
              <button className="close-popup" onClick={handleClosePopup}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="quotation-preview">
              <div className="preview-row">
                <strong><i className="fas fa-arrow-right"></i> Project:</strong>
                <span>{selectedInquiry.projectName}</span>
              </div>
              <div className="preview-row">
                <strong><i className="fas fa-boxes"></i> Quantity:</strong>
                <span>{selectedInquiry.requiredQuantity || 'N/A'} units</span>
              </div>
              <div className="preview-row">
                <strong><i className="fas fa-money-bill-wave"></i> Budget Range:</strong>
                <span>{formatBudget(selectedInquiry.expectedBudgetRange)}</span>
              </div>
              <div className="preview-row">
                <strong><i className="fas fa-calendar-check"></i> Delivery:</strong>
                <span>{formatDate(selectedInquiry.targetDeliveryDate)}</span>
              </div>
              <div className="preview-row">
                <strong><i className="fas fa-tag"></i> Category:</strong>
                <span>{formatCategory(selectedInquiry.productCategory)}</span>
              </div>
              <div className="preview-row">
                <strong><i className="fas fa-industry"></i> Process:</strong>
                <span>{formatProcesses(selectedInquiry.manufacturingProcesses)}</span>
              </div>
              <div className="preview-row note">
                <strong><i className="fas fa-comment-dots"></i> Note:</strong>
                <span>{selectedInquiry.shortDescription || 'No description'}</span>
              </div>
            </div>

            <form className="popup-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-heading"></i> Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Quotation subject"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group half">
                  <label className="form-label">
                    <i className="fas fa-rupee-sign"></i> Amount (₹)
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="e.g. 2450"
                    step="0.01"
                    required
                    min="0"
                  />
                </div>

                <div className="form-group half">
                  <label className="form-label">
                    <i className="fas fa-calendar-alt"></i> Valid Until
                  </label>
                  <input
                    type="date"
                    name="validity"
                    value={formData.validity}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-clock"></i> Estimated Delivery Days
                </label>
                <input
                  type="number"
                  name="deliveryDays"
                  value={formData.deliveryDays}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g. 20"
                  required
                  min="1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-envelope"></i> Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-input form-textarea"
                  placeholder="Add a note for the client..."
                  rows="4"
                />
              </div>

              {/* PDF Upload Section */}
              <div className="form-group">
                <label className="form-label">
                  <i className="fas fa-paperclip"></i> Attach Document (PDF/Word)
                </label>
                <div className="file-upload-container">
                  <div className="file-upload-area">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="file-input"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    />
                    <div className="file-upload-placeholder">
                      <i className="fas fa-cloud-upload-alt"></i>
                      <p>Click or drag to upload document</p>
                      <span className="file-hint">PDF, DOC, DOCX (Max 10MB)</span>
                    </div>
                  </div>

                  {formData.documentName && (
                    <div className="file-info">
                      <div className="file-name">
                        <i className="fas fa-file-pdf"></i>
                        <span>{formData.documentName}</span>
                      </div>
                      <button
                        type="button"
                        className="btn-remove-file"
                        onClick={handleRemoveFile}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={handleClosePopup}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Sending...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i> Send Quotation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiryComponent;